import { fmtPersonTime, parsePersonTime } from '@/utils/personTime';

/**
 * Tree/branch line model — 2026-09-03 rewrite (Alexey; backend commits
 * bfa16896/a5ac338b/4746ab31). Both "Выкладка"'s per-category blocks and
 * "Очередь и бар"'s "Самая длинная очередь" block share ONE rendering
 * primitive on the backend (`TreeBranch`/`render_tree_branches` in
 * composer.py): a header row ("{emoji} {title}", or a flat "{emoji}
 * {title}: {value}" one-liner) plus zero or more ├/└ branch rows, each
 * either labelled ("label: value") or bare ("value" only — the queue
 * block's two branches). A branch's value is `<u>`-wrapped exactly when it
 * represents a problem (composer.py's `is_problem`); connectors (├ vs └)
 * are ALWAYS recomputed from how many branches are actually present, never
 * stored — see `render_tree_branches`'s own docstring: "keeping each branch
 * as data (not baked-in ├/└ text) is what lets a future FE constructor
 * toggle branches on/off and have the connectors recompute". This module is
 * that FE half: `TreeStatBranch`/`renderTreeBranches` mirror the backend's
 * `TreeBranch`/`render_tree_branches` 1:1.
 *
 * Four concrete line shapes are recognised (services/.../daily_report/
 * sections/empty_shelf.py `build_category_tree` + checkout_control.py
 * `build_checkout_control_section_body`):
 *   - category_tree   — a "Выкладка" category block (build_category_tree)
 *   - longest_queue    — the "Самая длинная очередь" tree block
 *   - long_queue       — the "Очередь от N человек: ..." single line
 *   - no_visitors      — the "Без посетителей у кассы: ..." single line
 *
 * IMPORTANT: this is the NEW format only. Reports generated before today's
 * rewrite (either of keypointBlock.ts's OLD/NEW sentence variants) don't
 * contain any of this text — see `isTreeFormat` for the format detector
 * lineModel.ts/ConstructorView/InteractiveView gate structured editing on.
 */

// ── shared branch primitive (mirrors composer.py's TreeBranch) ────────────

export interface TreeStatBranch {
  label: string; // '' → bare branch (no "label: " prefix)
  value: string; // plain text/HTML, NOT pre-wrapped in <u> — isProblem drives that at render time
  isProblem: boolean;
}

/** Mirrors composer.py's `render_tree_branches` exactly: connectors are
 *  recomputed from how many branches are present, never stored. */
export function renderTreeBranches(branches: TreeStatBranch[]): string[] {
  const lastIdx = branches.length - 1;
  return branches.map((b, i) => {
    const connector = i === lastIdx ? '└' : '├';
    const value = b.isProblem ? `<u>${b.value}</u>` : b.value;
    return b.label ? `${connector} ${b.label}: ${value}` : `${connector} ${value}`;
  });
}

// A branch row's value is either `<u>...</u>`-wrapped (isProblem) or bare.
// The optional label is any run of non-':'/non-'<' text immediately before
// a literal ": " — this also correctly declines to match when the value
// itself starts with '<' (an <a>/<u> tag), since a real label never does.
const BRANCH_ROW_RE = /^(?:├|└)\s+(?:([^:<]+):\s*)?(?:<u>([\s\S]*?)<\/u>|([\s\S]*))$/u;

export function isTreeBranchRow(row: string): boolean {
  return BRANCH_ROW_RE.test(row.trim());
}

function parseBranchRow(row: string): TreeStatBranch {
  const m = BRANCH_ROW_RE.exec(row.trim());
  if (!m) return { label: '', value: row.trim(), isProblem: false };
  const label = m[1]?.trim() ?? '';
  const isProblem = m[2] !== undefined;
  const value = isProblem ? m[2] : (m[3] ?? '');
  return { label, value, isProblem };
}

/** Consume a header row + every following ├/└ row (used by both
 *  category_tree and longest_queue block grouping in lineModel.ts). */
export function consumeTreeBlockRows(rows: string[], startIdx: number): number {
  let end = startIdx;
  while (end + 1 < rows.length && isTreeBranchRow(rows[end + 1])) end++;
  return end;
}

// ── category block ("Выкладка", build_category_tree) ──────────────────────

const CATEGORY_EMOJI = '🔴|🟠|🟡|🟢|⚪';
// The rack type, optionally wrapped in the drill-down link (2026-09-06
// header format — see CategoryTreeFields.linkHref). Groups: href, type.
// A real rack_type never contains ':' or '<', which is what keeps every
// legacy colon-suffixed sentence line out of these matchers.
const LINKED_TYPE = `(?:<a\\s+[^>]*href=['"]([^'"]+)['"][^>]*>)?\\s*([^<:]+?)\\s*(?:<\\/a>)?`;
// Header shapes (all groups after the emoji: href, type, ...):
//   FLAT     — "{emoji} {type}: ОК" / "{emoji} {type}: за день нет ни одного измерения"
//              (the ОК one may be followed by opening/closing branches since 2026-09-06)
//   ISSUES   — "{emoji} {type}: ниже нормы <u>{по N полкам, }H.H ч</u>" (2026-09-06+)
//   BARE     — "{emoji} {type}" + a "├ Ниже нормы: …" branch (2026-09-03..05 vintage)
const CATEGORY_FLAT_RE = new RegExp(
  `^(${CATEGORY_EMOJI})\\s+${LINKED_TYPE}:\\s*(ОК|за день нет ни одного измерения)\\s*$`,
  'u',
);
const CATEGORY_ISSUES_HEADER_RE = new RegExp(
  `^(${CATEGORY_EMOJI})\\s+${LINKED_TYPE}:\\s*ниже нормы\\s+(?:<u>([\\s\\S]*?)<\\/u>|(.+?))\\s*$`,
  'u',
);
const CATEGORY_BARE_HEADER_RE = new RegExp(`^(${CATEGORY_EMOJI})\\s+${LINKED_TYPE}$`, 'u');
// «по 3 полкам, 10.2 ч» (09-06..15.09) or «3 полки 10.2 ч» (15.09+). Groups: old count, new count, hours.
const PROSADKI_RE = /^(?:по\s+(\d+)\s+(?:полке|полкам),\s*|(\d+)\s+(?:полка|полки|полок)\s+)?([\d.]+)\s*ч$/u;

export const LONGEST_QUEUE_TITLE = 'Самая длинная очередь';

export type CategoryVariant = 'ok' | 'no_data' | 'issues';

export interface CategoryTreeFields {
  colorEmoji: string;
  /** Latent «avg shortage ≥ 40%» behind a 🔴/🟠 header — the text carries hours
   *  and shelves but not the shortage %, so the colour is re-derived from this
   *  flag + hours (mirrors BE `_layout_color_emoji`). */
  shortageHigh: boolean;
  rackType: string;
  variant: CategoryVariant;
  // 'issues' only:
  shelfCount: number; // singular-vs-plural phrasing is derived from this (=== 1), no separate flag
  /** «по 1 полке» is written in the text. The BE drops the count only for a rack with a single shelf
   *  (Alexey 15.09: «убрать количество полок только для стеллажей с одной полкой»), so a missing count on
   *  re-render must stay missing, and one that is present must stay present. */
  showShelfCount: boolean;
  /** Latent «only a small share of the category's shelves is low» (BE SMALL_SHORTAGE_SHARE, 15.09 Alexey:
   *  «одна полка из 5 ниже нормы — зеленый кружок»). Read from the «Ниже нормы» branch not being underlined. */
  smallShare: boolean;
  problemHours: number;
  neverFilled: boolean;
  openingPct: number;
  closingPct: number;
  openingProblem: boolean; // forced true when neverFilled (see build_category_tree)
  closingProblem: boolean; // derived from closingPct + rackType, see deriveClosingSeverity — no manual flag
  filledAt: string; // HH:MM — "Вышла на норму" (openingProblem && !neverFilled only)
  replenishedAt: string; // HH:MM — "Пополнялась в последний раз" branch shown whenever non-empty, no separate flag
  // 2026-09-06 header format (empty_shelf.py `_category_header`): the rack
  // type is wrapped in the target=racks drill-down link on any non-green
  // header. Kept verbatim so an edit+save doesn't strip the link (its
  // from/to/rack params are what the web report's icon button opens).
  linkHref: string;
  // Whether the На открытии / Остаток на закрытии branches are present.
  // 'ok': the "ОК but opening/closing still worth a look" shape —
  // "{🟡|🟢} {type}: ОК" + branches (Alexey 2026-09-06: "'Готовая еда: ОК'
  // — нет деталей... надо оставлять пункты на открытии и к закрытию");
  // false → bare "🟢 {type}: ОК". 'issues': false = header-only block (a
  // category with no clarify_category wired, e.g. Десерты).
  details: boolean;
  /** 'ok' only: the «Ниже нормы не опускалась» branch is written. 21.10 (Alexey #7010/#7013): the BE drops it
   *  when the shelf dipped below norm outside the opening (raw samples, under the 15/30 gates), and a category
   *  without opening/closing details is the one-liner «🟢 {type}: ОК» instead of a lone «└ Ниже нормы…». */
  noShortageLine: boolean;
  /** «Посетителей в очереди за просадку: N чел·мин» (21.10, Alexey; BE layout_dips.py) —
   *  'issues' only, right under «Ниже нормы». null = no such branch (zone has no queue,
   *  or the validator removed it). */
  dipVisitorsPm: number | null;
}

export const DIP_VISITORS_LABEL = 'Посетителей в очереди за просадку';

const DEFAULT_ISSUE_FIELDS: Omit<CategoryTreeFields, 'colorEmoji' | 'rackType' | 'variant'> = {
  shortageHigh: false,
  shelfCount: 1,
  showShelfCount: false,
  smallShare: false,
  problemHours: 1,
  neverFilled: false,
  openingPct: 100,
  closingPct: 100,
  openingProblem: false,
  closingProblem: false,
  filledAt: '',
  replenishedAt: '',
  linkHref: '',
  details: true,
  noShortageLine: true,
  dipVisitorsPm: null,
};

const BAKERY_RACK_TYPE = 'Выпечка'; // mirrors empty_shelf.py's BAKERY_RACK_TYPE
const NEAR_EMPTY_PCT = 3; // mirrors empty_shelf.py's NEAR_EMPTY_PCT

/** Mirrors clarify_category's `closing_severity_of` (empty_shelf.py ~line
 *  191) exactly, MINUS `next_day_closed` — not available to the constructor
 *  (it's schedule context, not something this form has data for); a closing
 *  report edited the evening before a day off will over-flag as a result,
 *  same known gap as every other "constructor can't see backend-only
 *  context" case in this module. Alexey (2026-09-04): "'Проблема на
 *  закрытии' — тоже непонятное поле... мы просто ставим уровень
 *  заполненности, и исходя из него уже кружок считается сам" — no manual
 *  checkbox, always derived. */
export function deriveClosingSeverity(closingPct: number, rackType: string): 0 | 1 | 2 {
  const bakery = rackType === BAKERY_RACK_TYPE;
  if (bakery) {
    if (closingPct >= 80) return 2;
    if (closingPct >= 35) return 1; // 15.09 Alexey: «вечерний остаток 35% проблемой, 30 еще зеленый» (BE BAKERY_CLOSING_YELLOW_PCT)
    return 0;
  }
  if (closingPct <= NEAR_EMPTY_PCT) return 2;
  if (closingPct < 20) return 1;
  return 0;
}

/** True for a "Выкладка" category header row — EXCLUDES "Самая длинная
 *  очередь" (same emoji set, but that's the queue block, not a category).
 *
 * The same 5-colour emoji set is used by legacy sentence-format lines too
 * (e.g. keypointBlock.ts's NEW-format one-liner "🟢 Выпечка: на открытии
 * выкладка полная, ...", or the old "⚪ Посетителей не было у кассы...") —
 * a bare "emoji + anything" header regex would misclassify those. Two
 * disjoint, unambiguous shapes are accepted instead:
 *   - the FLAT one-liner, whose value is an exact match ("ОК" / "за день
 *     нет ни одного измерения") — no other format produces that text;
 *   - the BRANCH header, whose remaining text has NO colon at all (a real
 *     rack_type name never contains one — unlike every legacy sentence
 *     line, which always does) AND is immediately followed by a real ├/└
 *     branch row (build_category_tree always emits at least the "Ниже
 *     нормы" branch for this shape, so a real header is never branch-less). */
export function isCategoryTreeRow(row: string, nextRow: string | undefined): boolean {
  const trimmed = row.trim();
  if (CATEGORY_FLAT_RE.test(trimmed)) return true;
  if (CATEGORY_ISSUES_HEADER_RE.test(trimmed)) return true;
  const m = CATEGORY_BARE_HEADER_RE.exec(trimmed);
  if (!m) return false;
  if (m[3].trim() === LONGEST_QUEUE_TITLE) return false;
  return nextRow !== undefined && isTreeBranchRow(nextRow);
}

function firstNumber(s: string): number {
  const m = /\d+/.exec(s);
  return m ? Number(m[0]) : 0;
}

function hhmmToMinutes(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

/** Mirrors clarify_category's `opening_problem = not (hours_after_open == 0
 *  and mins_after_open <= 20)` exactly (empty_shelf.py ~line 394) — that
 *  condition simplifies to "filled more than 20 minutes after opening".
 *  Alexey (2026-09-04): "Проблема на открытии" was a vague manual checkbox
 *  disconnected from real data — take the backend logic 1:1 instead of
 *  approximating it off `openingPct` (which the backend doesn't use for
 *  this decision at all).
 *
 *  Returns null — "can't derive" — when either time is missing/unparseable
 *  (openTime unknown for this zone/date, or filledAt not yet entered): the
 *  caller falls back to the last explicit value in that case, it does NOT
 *  guess. */
export function deriveOpeningProblem(filledAt: string, openTime: string | null): boolean | null {
  if (!openTime) return null;
  const openMin = hhmmToMinutes(openTime);
  const filledMin = hhmmToMinutes(filledAt);
  if (openMin === null || filledMin === null) return null;
  const deltaMin = (((filledMin - openMin) % 1440) + 1440) % 1440;
  return deltaMin > 20;
}

/** The opening/closing facts carried by the ├/└ branches (mirrors
 *  `_opening_closing_branches` + build_category_tree's never_filled shape). */
function readOpeningClosing(branches: TreeStatBranch[]) {
  const byLabel = (...ls: string[]) => branches.find((b) => ls.includes(b.label));
  const opening = byLabel('На открытии');
  const closing = byLabel('Остаток на закрытии', 'К закрытию');
  const vyshla = byLabel('Вышла на норму', 'Вышла в норму');
  const popoln = byLabel('Последнее пополнение', 'Пополнялась в последний раз');
  if (!opening && !closing) return null;
  // Disambiguating never_filled vs the normal path (both can render just
  // "На открытии" + "Остаток на закрытии" with nothing else): in the normal
  // path, opening_problem=True ALWAYS ships a "Вышла на норму" branch
  // alongside it (filled_at is only null when never_filled is already true
  // — see clarify_category) — so underlined-opening WITHOUT that branch can
  // only be never_filled (which hardcodes opening's is_problem=True
  // regardless). Likewise "почти не было на прилавке" is a never_filled-only
  // phrase — the normal path's "Остаток на закрытии" is always a plain
  // "{pct}%".
  const closingIsWords = closing?.value.trim() === 'почти не было на прилавке';
  const neverFilled = closingIsWords || (Boolean(opening?.isProblem) && !vyshla);
  return {
    neverFilled,
    openingPct: opening ? firstNumber(opening.value) : 0,
    closingPct: closingIsWords ? 10 : closing ? firstNumber(closing.value) : 0,
    openingProblem: neverFilled ? true : Boolean(opening?.isProblem),
    closingProblem: Boolean(closing?.isProblem),
    filledAt: vyshla?.value.trim() ?? '',
    replenishedAt: popoln?.value.trim() ?? '',
  };
}

export function parseCategoryTreeFields(rows: string[]): CategoryTreeFields {
  const header = rows[0].trim();
  const branches = rows.slice(1).map(parseBranchRow);
  const flatM = CATEGORY_FLAT_RE.exec(header);
  if (flatM) {
    const variant: CategoryVariant = flatM[4] === 'ОК' ? 'ok' : 'no_data';
    const oc = variant === 'ok' ? readOpeningClosing(branches) : null;
    return {
      ...DEFAULT_ISSUE_FIELDS,
      ...(oc ?? {}),
      colorEmoji: flatM[1],
      linkHref: flatM[2] ?? '',
      rackType: flatM[3].trim(),
      variant,
      details: oc !== null,
    };
  }

  // ISSUES header (2026-09-06+): "ниже нормы" lives in the header itself.
  // BARE header (09-03..05 vintage): it's the first "Ниже нормы"/"Просадки"
  // branch instead — accepted on parse; serialize only ever emits the new
  // shape, so any edit+save converts a report forward.
  const issM = CATEGORY_ISSUES_HEADER_RE.exec(header);
  const bareM = issM ? null : CATEGORY_BARE_HEADER_RE.exec(header)!;
  // 15.09 (Alexey): an OK day is «{emoji} {type}» + «├ Ниже нормы не опускалась» (was «{type}: ОК»).
  // 21.10: an OK day with the line dropped — a bare header with no «Ниже нормы»/«Просадки» branch at all.
  const noProsadkiBranch = !branches.some((b) => b.label === 'Ниже нормы' || b.label === 'Просадки');
  if (bareM && !(branches[0]?.label === '' && branches[0].value === NO_SHORTAGE_TEXT) && noProsadkiBranch) {
    const oc = readOpeningClosing(branches);
    return {
      ...DEFAULT_ISSUE_FIELDS,
      ...(oc ?? {}),
      colorEmoji: bareM[1],
      linkHref: bareM[2] ?? '',
      rackType: bareM[3].trim(),
      variant: 'ok',
      details: oc !== null,
      noShortageLine: false,
    };
  }
  if (bareM && branches[0]?.label === '' && branches[0].value === NO_SHORTAGE_TEXT) {
    const oc = readOpeningClosing(branches);
    return {
      ...DEFAULT_ISSUE_FIELDS,
      ...(oc ?? {}),
      colorEmoji: bareM[1],
      linkHref: bareM[2] ?? '',
      rackType: bareM[3].trim(),
      variant: 'ok',
      details: oc !== null,
    };
  }
  const headM = (issM ?? bareM)!;
  const prosadkiText = issM ? (issM[4] ?? issM[5] ?? '').trim() : null;
  // 2026-09-04 (Alexey label rename, item 2): accepts EITHER the new label
  // or the pre-rename one still sitting in already-generated reports from
  // 09-03 — serializeCategoryTree below only ever emits the new label, so
  // any edit+save converts a report forward. Drop the old alternatives once
  // no un-edited 09-03-vintage report is expected to need the constructor
  // anymore.
  const byLabel = (...ls: string[]) => branches.find((b) => ls.includes(b.label));

  const prosadki = prosadkiText === null ? byLabel('Ниже нормы', 'Просадки') : null;
  const prosadkiValue = prosadkiText ?? prosadki?.value.trim() ?? '';
  const prosadkiM = PROSADKI_RE.exec(prosadkiValue);
  const oc = readOpeningClosing(branches);
  const dipPm = parsePersonTime(byLabel(DIP_VISITORS_LABEL)?.value ?? '');

  return {
    ...DEFAULT_ISSUE_FIELDS,
    ...(oc ?? {}),
    dipVisitorsPm: dipPm,
    colorEmoji: headM[1],
    shortageHigh: headM[1] === '🔴' || headM[1] === '🟠',
    linkHref: headM[2] ?? '',
    rackType: headM[3].trim(),
    variant: 'issues',
    details: oc !== null,
    shelfCount: Number(prosadkiM?.[1] ?? prosadkiM?.[2] ?? 1),
    showShelfCount: (prosadkiM?.[1] ?? prosadkiM?.[2]) !== undefined,
    // Only the 15.09+ wording («1 полка 5.0 ч») can carry a small share: in 09-03 vintage reports the «Ниже нормы»
    // branch was never underlined, and a single-shelf rack («5.0 ч») is always 1 of 1, i.e. never a small share.
    smallShare: prosadki !== null && prosadki !== undefined && !prosadki.isProblem && prosadkiM?.[2] !== undefined,
    problemHours: prosadkiM ? Number(prosadkiM[3]) : 0,
  };
}

const NO_SHORTAGE_TEXT = 'Ниже нормы не опускалась';
const NO_SHORTAGE_BRANCH: TreeStatBranch = { label: '', value: NO_SHORTAGE_TEXT, isProblem: false };

/** Mirrors BE `_closing_value` (15.09 Alexey: «пишем 30% (много)»): bakery problem = too much left, others = too little. */
function closingValue(f: CategoryTreeFields): string {
  const pct = `${Math.round(f.closingPct)}%`;
  if (!f.closingProblem) return pct;
  return `${pct} ${f.rackType === BAKERY_RACK_TYPE ? '(много)' : '(мало)'}`;
}

/** Mirrors BE `_ru_shelves`: «1 полка», «2 полки», «5 полок», «11 полок». */
function ruShelves(n: number): string {
  const d = n % 10;
  const h = n % 100;
  const word = d === 1 && h !== 11 ? 'полка' : d >= 2 && d <= 4 && !(h >= 12 && h <= 14) ? 'полки' : 'полок';
  return `${n} ${word}`;
}

/** Mirrors `_opening_closing_branches` (the non-never_filled path). */
function openingClosingBranches(f: CategoryTreeFields): TreeStatBranch[] {
  const branches: TreeStatBranch[] = [
    { label: 'На открытии', value: `${Math.round(f.openingPct)}%`, isProblem: f.openingProblem },
  ];
  // Mirrors the `clarification.filled_at is not None` guard exactly — the
  // backend only emits this branch when it actually has a clock value,
  // never on openingProblem alone. Without the `f.filledAt` half of this
  // check, toggling `neverFilled` off in the constructor (which leaves
  // openingProblem=true behind — see readOpeningClosing's
  // never_filled-forces-true case) rendered a broken "└ Вышла в норму:
  // <u></u>" branch with no time at all.
  if (f.openingProblem && f.filledAt) {
    branches.push({ label: 'Вышла на норму', value: f.filledAt, isProblem: true });
  }
  if (f.closingProblem && f.replenishedAt) {
    // Renamed from «Пополнялась в последний раз» (Alexey 2026-09-08); the parser still reads both.
    branches.push({ label: 'Последнее пополнение', value: f.replenishedAt, isProblem: true });
  }
  branches.push({
    label: 'Остаток на закрытии',
    value: closingValue(f),
    isProblem: f.closingProblem,
  });
  return branches;
}

/** Mirrors `_category_header`: the drill-down link wraps the rack type on
 *  any non-green header (there's nothing to look into on a plain 🟢). */
function headerType(f: CategoryTreeFields): string {
  return f.linkHref && f.colorEmoji !== '🟢' ? `<a href='${f.linkHref}'>${f.rackType}</a>` : f.rackType;
}

/** Mirrors build_category_tree + render_tree_branches exactly (2026-09-06
 *  header format: "ниже нормы …" in the header, ОК may carry branches). */
export function serializeCategoryTree(f0: CategoryTreeFields): string[] {
  let f = f0;
  if (f.variant === 'no_data') return [`⚪ ${f.rackType}: за день нет ни одного измерения`];
  if (f.variant === 'ok') {
    if (!f.details) return [f.noShortageLine ? `🟢 ${f.rackType}: ОК` : `🟢 ${f.rackType}`];
    // Mirrors BE `ok_color`: a flagged (underlined) opening or closing branch
    // makes the circle at least 🟡 — a green circle over an underlined
    // "Остаток на закрытии: 75%" is what Alexey flagged on Deka 2026-09-08.
    // A stronger manual colour (🟠/🔴) is kept.
    const flagged = f.openingProblem || f.closingProblem;
    const colour = flagged ? '🟡' : '🟢';
    const g = { ...f, colorEmoji: colour };
    const noShortage = f.noShortageLine ? [NO_SHORTAGE_BRANCH] : [];
    return [`${colour} ${headerType(g)}`, ...renderTreeBranches([...noShortage, ...openingClosingBranches(g)])];
  }

  const prosadkiValue =
    f.shelfCount === 1 && !f.showShelfCount
      ? `${f.problemHours.toFixed(1)} ч`
      : `${ruShelves(f.shelfCount)} ${f.problemHours.toFixed(1)} ч`;
  // Colour is never hand-picked (Alexey 2026-09-08: «кружки всегда автоматом,
  // разметчики делают только параметры»): mirrors BE build_category_tree.
  const flagged = f.details && (f.neverFilled || f.openingProblem || f.closingProblem);
  const colour = f.smallShare
    ? flagged
      ? '🟡'
      : '🟢'
    : f.shortageHigh
      ? f.problemHours >= 10
        ? '🔴'
        : '🟠'
      : '🟡';
  f = { ...f, colorEmoji: colour };
  // 15.09 (Alexey): back to «🟢 Готовая еда» + the «├ Ниже нормы: 1 полка 5.0 ч» branch.
  const header = `${colour} ${headerType(f)}`;
  const branches: TreeStatBranch[] = [{ label: 'Ниже нормы', value: prosadkiValue, isProblem: !f.smallShare }];
  if (f.dipVisitorsPm != null) {
    branches.push({ label: DIP_VISITORS_LABEL, value: fmtPersonTime(f.dipVisitorsPm), isProblem: false });
  }

  if (!f.details) {
    // header-only block (no opening/closing clarification wired)
  } else if (f.neverFilled) {
    branches.push({ label: 'На открытии', value: `${Math.round(f.openingPct)}%`, isProblem: true });
    const closingWords = f.closingPct <= 15;
    branches.push({
      label: 'Остаток на закрытии',
      value: closingWords ? 'почти не было на прилавке' : closingValue(f),
      isProblem: f.closingProblem,
    });
  } else {
    branches.push(...openingClosingBranches(f));
  }

  return [header, ...renderTreeBranches(branches)];
}

// ── "Самая длинная очередь" block ──────────────────────────────────────────

const PEAK_EMOJI = '🟠|🟡|🟢';
// 2026-09-06 (checkout_control.py): the target=queue drill-down link moved
// AGAIN — off the "Спала за N мин" branch onto the header itself ("🟡 <a
// href=…>Самая длинная очередь</a>"), matching the category headers. The
// header regex accepts an optional wrapping <a> (group 2 = href); the
// branch regexes below keep accepting a link too, so both stored shapes
// (2026-09-03 people-branch, 09-04..05 drain-branch) still parse — the
// serializer only ever writes the header form, converting them forward.
const LONGEST_HEADER_RE = new RegExp(
  `^(${PEAK_EMOJI})\\s+(?:<a\\s+[^>]*href=['"]([^'"]+)['"][^>]*>)?\\s*${LONGEST_QUEUE_TITLE}\\s*(?:<\\/a>)?\\s*$`,
  'u',
);
// 2026-09-04 (Alexey, verbatim): "диплинк на target=queue надо поставить на
// текст 'Спала за N минут'" — the target=queue drill-down link moved from
// the "N человек" branch onto "Спала за N мин" (checkout_control.py). Both
// regexes still accept an optional wrapping <a> so an already-stored
// pre-move report (link on the people branch) still parses — see linkUrl
// below; serializeLongestQueue only ever writes it onto the drain branch, so
// any edit+save converts a report forward, same convention as the
// category-tree label rename above.
const LONGEST_PEOPLE_RE =
  /^(?:<a\s+[^>]*href=['"]([^'"]+)['"][^>]*>)?\s*(\d+)\s+человек[а]?\s*(?:<\/a>)?$/u;
const LONGEST_DRAIN_RE =
  /^(?:<a\s+[^>]*href=['"]([^'"]+)['"][^>]*>)?\s*(?:Спала за\s+(\d+)\s+мин|Время спада не измерено)\s*(?:<\/a>)?$/u;
const LONGEST_DRAIN_UNMEASURED = 'Время спада не измерено';

export interface LongestQueueFields {
  colorEmoji: string;
  isProblem: boolean; // "N человек" branch's is_problem
  peopleCount: number;
  // The block's target=queue drill-down link — lives on the HEADER since
  // 2026-09-06 (see LONGEST_HEADER_RE). Parsed off the header or either
  // branch for back-compat; always serialized onto the header.
  linkUrl: string | null;
  drainMinutes: number | null; // null → drain never observed ("не измерено")
}

/** Same branch-following requirement as `isCategoryTreeRow` (belt-and-
 *  braces — the fixed "Самая длинная очередь" literal is already
 *  near-unambiguous on its own, but a title-only match with no branches
 *  following can't be a real block either way). */
export function isLongestQueueRow(row: string, nextRow: string | undefined): boolean {
  return LONGEST_HEADER_RE.test(row.trim()) && nextRow !== undefined && isTreeBranchRow(nextRow);
}

export function parseLongestQueueFields(rows: string[]): LongestQueueFields {
  const headM = LONGEST_HEADER_RE.exec(rows[0].trim())!;
  const branches = rows.slice(1).map(parseBranchRow);
  const peopleB = branches[0];
  const drainB = branches[1];
  const peopleM = peopleB ? LONGEST_PEOPLE_RE.exec(peopleB.value.trim()) : null;
  const drainM = drainB ? LONGEST_DRAIN_RE.exec(drainB.value.trim()) : null;

  return {
    colorEmoji: headM[1],
    isProblem: Boolean(peopleB?.isProblem),
    peopleCount: peopleM ? Number(peopleM[2]) : 0,
    // Header link (current shape) → drain branch (09-04..05) → people branch
    // (09-03) — see the LONGEST_HEADER_RE comment.
    linkUrl: headM[2] ?? drainM?.[1] ?? peopleM?.[1] ?? null,
    drainMinutes: drainM?.[2] !== undefined ? Number(drainM[2]) : null,
  };
}

/** "N человек" agreement (1/5+/11-14 → человек, 2-4 → человека) — Alexey
 *  2026-09-04 ("people-count declines grammatically"); backend mirrors via
 *  checkout_control.py's _ru_people. */
export function ruPeople(n: number): string {
  const n100 = n % 100;
  const n10 = n % 10;
  const word = n100 >= 11 && n100 <= 14 ? 'человек' : n10 >= 2 && n10 <= 4 ? 'человека' : 'человек';
  return `${n} ${word}`;
}

/** "N часов" agreement — same rule shape as ruMinutes; backend _ru_hours. */
export function ruHours(n: number): string {
  const n100 = n % 100;
  const n10 = n % 10;
  const word = n100 >= 11 && n100 <= 14 ? 'часов' : n10 === 1 ? 'час' : n10 >= 2 && n10 <= 4 ? 'часа' : 'часов';
  return `${n} ${word}`;
}

// ── Auto-colour rules (mirror checkout_control.py's _PEAK_QUEUE_* /
// _LONG_QUEUE_MINUTES_* / _NO_VISITORS_SHARE_* 1:1). The constructor's
// in-place rows compute these live from the values and let the validator
// override (Alexey 2026-09-04: "auto-color... computed live but overridable").
const PEAK_QUEUE_YELLOW_AT = 5;
const PEAK_QUEUE_ORANGE_AT = 8;
const LONG_QUEUE_MINUTES_YELLOW_AT = 30;
const LONG_QUEUE_MINUTES_ORANGE_AT = 60;

export function derivePeakQueueColor(people: number): string {
  if (people >= PEAK_QUEUE_ORANGE_AT) return '🟠';
  if (people >= PEAK_QUEUE_YELLOW_AT) return '🟡';
  return '🟢';
}

export function deriveLongQueueColor(minutes: number, none: boolean): string {
  if (none || minutes <= 0) return '🟢';
  if (minutes >= LONG_QUEUE_MINUTES_ORANGE_AT) return '🟠';
  if (minutes >= LONG_QUEUE_MINUTES_YELLOW_AT) return '🟡';
  return '🟢';
}

/** Mirrors build_checkout_control_section_body's `longest_branches` exactly. */
export function serializeLongestQueue(f0: LongestQueueFields): string[] {
  // Colour always derived from the people count (Alexey 2026-09-08).
  const auto = derivePeakQueueColor(f0.peopleCount);
  const f = { ...f0, colorEmoji: auto, isProblem: auto !== '🟢' };
  const peopleValue = ruPeople(f.peopleCount);
  const drainValue =
    f.drainMinutes != null ? `Спала за ${Math.max(1, f.drainMinutes)} мин` : LONGEST_DRAIN_UNMEASURED;
  const branches: TreeStatBranch[] = [
    { label: '', value: peopleValue, isProblem: f.isProblem },
    { label: '', value: drainValue, isProblem: false },
  ];
  const title = f.linkUrl ? `<a href='${f.linkUrl}'>${LONGEST_QUEUE_TITLE}</a>` : LONGEST_QUEUE_TITLE;
  return [`${f.colorEmoji} ${title}`, ...renderTreeBranches(branches)];
}

// ── "Очередь от N человек" line ────────────────────────────────────────────

const LQ_EMOJI = '🔴|🟠|🟡|🟢';
const LQ_NONE_RE = new RegExp(`^(${LQ_EMOJI})\\s+Очередь от\\s+(\\d+)\\s+человек не наблюдалась\\s*$`, 'u');
const LQ_MAIN_RE = new RegExp(
  `^(${LQ_EMOJI})\\s+Очередь от\\s+(\\d+)\\s+человек:\\s*` +
    `(?:<u>([\\s\\S]*?)<\\/u>|([\\s\\S]*?))` +
    // 2026-09-07 (Alexey): "… раз (N минут)" — how long the second register
    // actually worked, in brackets after the event count. Optional on parse
    // (older stored rows have only "раз").
    `(?:,\\s*открыли вторую кассу:\\s*(\\d+)\\s*раз(?:\\s*\\((\\d+)\\s+минут[аыу]?\\))?)?\\s*$`,
  'u',
);

export interface LongQueueFields {
  colorEmoji: string;
  threshold: number;
  none: boolean; // "не наблюдалась" — no minutes value at all
  minutes: number;
  isProblem: boolean;
  secondCashierOn: boolean;
  secondCashierEvents: number;
  /** Minutes the second register was open during long-queue time (0 → omitted). */
  secondCashierMinutes: number;
}

/** 2026-09-08 (Alexey): the second-register time is a standard tree branch
 *  under the header — "└ Вторая касса была открыта: 21 минуту" — not a
 *  ", открыли вторую кассу: N раз (M минут)" tail. The event count is gone. */
const LQ_SECOND_RE = /^└\s*Вторая касса была открыта:\s*(\d+)\s*минут[аыу]?\s*$/u;

export function isLongQueueRow(row: string): boolean {
  const t = row.trim();
  return LQ_NONE_RE.test(t) || LQ_MAIN_RE.test(t);
}

export function isLongQueueSecondCashierRow(row: string | undefined): boolean {
  return row !== undefined && LQ_SECOND_RE.test(row.trim());
}

/** Russian numeral-noun agreement for minutes, mirrors backend `_ru_minutes`
 *  (queue_photo_overlay.py) exactly — 11-14 are always the "many" form. */
export function ruMinutes(n: number): string {
  const n100 = n % 100;
  const n10 = n % 10;
  let word: string;
  if (n100 >= 11 && n100 <= 14) word = 'минут';
  else if (n10 === 1) word = 'минуту';
  else if (n10 >= 2 && n10 <= 4) word = 'минуты';
  else word = 'минут';
  return `${n} ${word}`;
}

export function parseLongQueueFields(row: string, secondRow?: string): LongQueueFields {
  const t = row.trim();
  const noneM = LQ_NONE_RE.exec(t);
  if (noneM) {
    return {
      colorEmoji: noneM[1],
      threshold: Number(noneM[2]),
      none: true,
      minutes: 0,
      isProblem: false,
      secondCashierOn: false,
      secondCashierEvents: 0,
      secondCashierMinutes: 0,
    };
  }
  const m = LQ_MAIN_RE.exec(t)!;
  const isProblem = m[3] !== undefined;
  const minutesText = (isProblem ? m[3] : (m[4] ?? '')).trim();
  const branch = secondRow !== undefined ? LQ_SECOND_RE.exec(secondRow.trim()) : null;
  return {
    colorEmoji: m[1],
    threshold: Number(m[2]),
    none: false,
    minutes: firstNumber(minutesText),
    isProblem,
    secondCashierOn: branch !== null || m[5] !== undefined,
    secondCashierEvents: m[5] !== undefined ? Number(m[5]) : 0,
    secondCashierMinutes: branch !== null ? Number(branch[1]) : m[6] !== undefined ? Number(m[6]) : 0,
  };
}

/** Mirrors build_checkout_control_section_body's `long_queue_line` exactly. */
export function serializeLongQueue(f0: LongQueueFields): string {
  // Colour always derived from the minutes (Alexey 2026-09-08).
  const auto = deriveLongQueueColor(f0.minutes, f0.none);
  const f = { ...f0, colorEmoji: auto, isProblem: !f0.none && auto !== '🟢' };
  if (f.none) return `${f.colorEmoji} Очередь от ${f.threshold} человек не наблюдалась`;
  const minutesTxt = ruMinutes(Math.max(0, Math.round(f.minutes)));
  const value = f.isProblem ? `<u>${minutesTxt}</u>` : minutesTxt;
  let row = `${f.colorEmoji} Очередь от ${f.threshold} человек: ${value}`;
  // New format (2026-09-08): the branch carries only the minutes; legacy
  // ", открыли вторую кассу: N раз (M минут)" rows are re-serialised into it.
  const mins = Math.max(0, Math.round(f.secondCashierMinutes));
  if (f.secondCashierOn && mins > 0) row += `\n└ Вторая касса была открыта: ${ruMinutes(mins)}`;
  return row;
}

// ── "Без посетителей у кассы" line ─────────────────────────────────────────

// Alexey-approved 2026-09-06 ("Без посетителей: Желтый давай от 50,
// оранжевый от 70, красный от 90"), mirrors checkout_control.py's
// _NO_VISITORS_SHARE_* — the 0.4/0.6 the constructor shipped with were the
// earlier PROPOSED values and had drifted from the backend.
const NO_VISITORS_SHARE_YELLOW_AT = 0.5;
const NO_VISITORS_SHARE_ORANGE_AT = 0.7;
const NO_VISITORS_SHARE_RED_AT = 0.9;

export function deriveNoVisitorsColor(zeroHours: number, totalHours: number): string {
  // Backend shows max(zero, 1) — a 0-hour day still reads "1 час" there.
  const shown = Math.max(zeroHours, 1);
  const share = totalHours > 0 ? shown / totalHours : 0;
  if (share >= NO_VISITORS_SHARE_RED_AT) return '🔴';
  if (share >= NO_VISITORS_SHARE_ORANGE_AT) return '🟠';
  if (share >= NO_VISITORS_SHARE_YELLOW_AT) return '🟡';
  return '🟢';
}

// ⚪ accepted on input only — every report before this colour rewrite hard-
// coded it regardless of share; serializeNoVisitors below never emits it.
const NO_VISITORS_EMOJI = '🔴|🟢|🟡|🟠|⚪';
const NO_VISITORS_RE = new RegExp(
  `^(${NO_VISITORS_EMOJI})\\s+Без посетителей у кассы:\\s*` +
    `(?:<u>(\\d+)\\s+час(?:ов|а)?\\s+из\\s+(\\d+)<\\/u>|(\\d+)\\s+час(?:ов|а)?\\s+из\\s+(\\d+))\\s*$`,
  'u',
);

export interface NoVisitorsFields {
  zeroHours: number;
  totalHours: number;
  /** The row's colour. Parsed as-is (a legacy ⚪ reads as the derived
   *  colour); the in-place row keeps it equal to deriveNoVisitorsColor
   *  unless the validator overrides it. */
  colorEmoji: string;
}

export function isNoVisitorsRow(row: string): boolean {
  return NO_VISITORS_RE.test(row.trim());
}

export function parseNoVisitorsFields(row: string): NoVisitorsFields {
  const m = NO_VISITORS_RE.exec(row.trim())!;
  const zeroHours = Number(m[2] !== undefined ? m[2] : m[4]);
  const totalHours = Number(m[2] !== undefined ? m[3] : m[5]);
  const colorEmoji = m[1] === '⚪' ? deriveNoVisitorsColor(zeroHours, totalHours) : m[1];
  return { zeroHours, totalHours, colorEmoji };
}

/** Mirrors build_checkout_control_section_body's `no_visitors_line` exactly
 *  (colour/underline derived from zeroHours/totalHours, not stored — same
 *  convention as every other derived field in this module). */
export function serializeNoVisitors(f: NoVisitorsFields): string {
  const zeroHours = Math.max(0, Math.round(f.zeroHours));
  const totalHours = Math.max(0, Math.round(f.totalHours));
  // Colour always derived from the share (Alexey 2026-09-08).
  const color = deriveNoVisitorsColor(zeroHours, totalHours);
  const value = `${ruHours(zeroHours)} из ${totalHours}`;
  return `${color} Без посетителей у кассы: ${color === '🟢' ? value : `<u>${value}</u>`}`;
}
